import { useDispatch } from "react-redux";
import { useSelector } from "store/useSelector";
import { updateLotInfo } from "providers/GlobalStateProvider";
import { useState, useEffect, useMemo } from "react";
import type { LotInfoType } from "types/types";
import { usePrevious } from "constants/constants";
import { isEqual } from "lodash";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import {
  useIsSingleProcessPage,
  useVisibleProcesses,
} from "utils/customHooks/genericHooks";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";

export const LOT_INFO_BAR_HEIGHT = 74;

export const EMPTY_INITIAL_LOT_INFO: LotInfoType = {
  currentLotRowID: "",
  lotStartDateMs: null,
  lotNumber: "",
  nextLotNumber: "",
  lotSize: 0,
  nextLotSize: 0,
};
export const EMPTY_LOT_ROW: LotFormDataType = {
  machemetryId: "",
  lotNumber: "",
  lotStartDateMs: "",
  lotSize: "",
  isCompleted: false,
};
const LOT_SCRIPT_URLS_BY_PROCESS = {
  // --- ravendemo ---
  ravendemo_d_001:
    "https://script.google.com/macros/s/AKfycbxhGe25x91SDta0y_U98V1b3J0bC27eYbGwKJQaZGYtdbwRK4ZY46WH4BuvR4wAph_f3Q/exec",

  // -- pci ---

  // G4
  pci_d_017:
    "https://script.google.com/macros/s/AKfycbyyALVu6ixduzMpYRFwPNN7hiLeYJaILMfUBmCDZBFNfGkV2lyWqCa8vPfWveJL2P7D/exec",

  // --- pciuk ---

  // 2A
  pciuk_d_001:
    "https://script.google.com/macros/s/AKfycbwa0TMyZr_WLt4HPpjTtBgVb3hohZZ8VC2ighDj-EOtB3L26kB02lqJ/exec",
  // 7A
  pciuk_d_002:
    "https://script.google.com/macros/s/AKfycbxMAPSG-5AGLjs2IL186KiC-wqLop6FuGHWaKLt-jgrnM0CymPyvzBDCw/exec",
  // 7B
  pciuk_d_003:
    "https://script.google.com/macros/s/AKfycbzTSwVV8FL_qDsJlzEAc6oZhxNUmCdTHIW5ejSb-wo0HNurKnmkjqXI8A/exec",
  // 7C
  pciuk_d_004:
    "https://script.google.com/macros/s/AKfycbwjsbjCexK4bxEwNI4sBmYHpkHn4LZegXX8YzVkWa9VFdiPcewg4-gg/exec",
  // 7F
  pciuk_d_005:
    "https://script.google.com/macros/s/AKfycbxdCOYJU3Qj-DMFaaUKK92561LsrykIHIuhSszj1j1hg9caIHsdLtF7TQ/exec",
  // 7E
  pciuk_d_006:
    "https://script.google.com/macros/s/AKfycbzlF0HtWnBdzdTav3vv_U-3ZDotD0B9HsyIoEnfjSJ0joZv0Qlld0H9/exec",
  // 5Y
  pciuk_d_007:
    "https://script.google.com/macros/s/AKfycbzC8OXDTxgPyRGqPRz0z7Si_ER5qoWDhZIjlESt6v6ifXHLpCfi3hiDCg/exec",
  // 5W
  pciuk_d_008:
    "https://script.google.com/macros/s/AKfycbzc1Q47B92wPy4w1qxI9XfLoZmjggeOPulAaIbGEcXnPkMyhXbINDhJ/exec",
  // 4L
  pciuk_d_009:
    "https://script.google.com/macros/s/AKfycbwfXsnNesOJzr43jks-Mx1YlmvvdrJtxE9OLvY9RWczm3LGhMKzmtXx/exec",
  // 4S
  pciuk_d_010:
    "https://script.google.com/macros/s/AKfycbxcdl0kR5K30Fxz0DpUuSxCuhHN4Kl2ZJXFRph-Dw1J9KOe4RDkai4A/exec",

  // --- renaissance ---
  renaissance_d_001:
    "https://script.google.com/macros/s/AKfycbxkbg2HDbmWOGzv7f6ZvdAiN7K9wuI23w7vWXXWXESoJK29siGy4N9aZxvbTYoJgmirYw/exec",
  renaissance_d_002:
    "https://script.google.com/macros/s/AKfycbwreYS-3iXjo28DHDYStBi2tAWJ7OeszL4J-CS0sevLtL6d3pGmOJOtYhiLPmK6u9L-fA/exec",
  renaissance_d_003:
    "https://script.google.com/macros/s/AKfycbzanA7ar50K4t0osHQHq_Jmymlw11GpBCAeIr08cVr8RxOrAGyX3wxNLK9bMmn373vZ/exec",
  renaissance_d_004:
    "https://script.google.com/macros/s/AKfycbw5vEjZME-evXdCd1HxlG3jsEmWyuxDXForLTSC8yAuK05PKYb_tGmrsQpxYvquUfmm/exec",
  renaissance_d_005:
    "https://script.google.com/macros/s/AKfycbyWJ9h0vQ2sc7e8lHgV9kjJic4ZSc4SVnfaQN0f_QtkwpnKZSHe1HCYKzpYLOpfE4Hm/exec",
  renaissance_d_006:
    "https://script.google.com/macros/s/AKfycbzbkC1ZZM71q3mUpPd38dnhhu8KDpoUlYUMhALfSaf6cnpzi6ODFSXxuxHtYFEC2aT8AA/exec",
  renaissance_d_007:
    "https://script.google.com/macros/s/AKfycbyJNGrksePxGBSRv7BG7DVMc7M2PYYvNUv5un9C22RMWvUsrP4sLKkQJCkAI3EflGvGlw/exec",
  renaissance_d_008:
    "https://script.google.com/macros/s/AKfycbyPdl8huTm65s0feyLM137o9BF1FJ9giaa7l6nBOqyCr7_onYIRraTIN45usGK2O5zkTg/exec",
  renaissance_d_009:
    "https://script.google.com/macros/s/AKfycbyIKwKQEOIGkSJuqkSdVMU__eVNZ9xCjf-0A1dnCazU3WzNVGFcc-tZuvchJp5xxC2r/exec",
  renaissance_d_010:
    "https://script.google.com/macros/s/AKfycbzxx7h3vh9tJAPVp7en3FA92vTnoGsBx8qhrnc1e2FQFE0xN2weoXOGWVTjwoSBrEfr/exec",
  renaissance_d_011:
    "https://script.google.com/macros/s/AKfycbyYksSxu6c4YAGMVJ2b6mnRlrLuMjc0_OCfnBT-Gceer6q25C3_d0tZB2NRC2IAOcEqNw/exec",
};

type UseLotInfoReturnType = {
  lotInfoForProcess: LotInfoType;
  submitEditedLotInfo: (lotInfo: LotInfoType) => any;
  completeTheLot: () => Promise<any>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
};
/** state / setState / isLoading from the global state store */
export const useLotInfoForProcess = (): UseLotInfoReturnType => {
  const isStorybook = useIsStorybook();
  const dispatch = useDispatch();
  const processId = useProcessIdOnSingleProcessPage();
  const [loading, setLoading] = useState(!isStorybook);
  const lotInfoForProcess = useLotInfoForProcessId(processId);

  const prevLotInfo = usePrevious(lotInfoForProcess);
  // stop loading when new data comes in
  useEffect(() => {
    if (prevLotInfo && !isEqual(prevLotInfo, lotInfoForProcess)) {
      setLoading(false);
    }
  }, [prevLotInfo, lotInfoForProcess]);

  /** Submit lotInfo to the database, then save it to the global state store
   *
   * Submit rows to "edit" the last two rows (really just submits two new rows to the DB)
   * (we can only edit lotNumber and lotSize)
   *
   */
  const submitEditedLotInfo = async (lotInfoEdited: LotInfoType) => {
    setLoading(true);
    const { lotNumber, nextLotNumber, lotSize, nextLotSize, lotStartDateMs } =
      lotInfoEdited;

    // edit the latest two rows (just adds two new rows to the sheet)
    const currentLotFormDataEdited = {
      machemetryId: processId ?? "",
      lotSize: lotSize ? String(lotSize) : "",
      lotNumber: lotNumber ? String(lotNumber) : "",
      lotStartDateMs: lotStartDateMs ? String(lotStartDateMs) : "",
      isCompleted: false, // hasn't finished yet
    };

    const nextLotFormDataEdited = {
      machemetryId: processId ?? "",
      lotSize: nextLotSize ? String(nextLotSize) : "",
      lotNumber: nextLotNumber ? String(nextLotNumber) : "",
      lotStartDateMs: "", // hasn't started yet
      isCompleted: false,
    };

    // submit to gsheet
    await handleFormSubmit(currentLotFormDataEdited);
    await handleFormSubmit(nextLotFormDataEdited);

    // save to store
    dispatch(
      updateLotInfo({ processId: processId ?? "", lotInfo: lotInfoEdited })
    );

    return true;
  };

  /** Submit three rows to the back-end: the completed lot, the new current lot, and the new next lot
   * The first two new rows are duplicates of the previous two
   *
   * 1. current lot becomes isCompleted: true
   *
   * 2. nextLotInfo becomes the current lotInfo
   *
   * 3. lotStartDateMs becomes now
   *
   */
  const completeTheLot = async () => {
    const now = Date.now();
    setLoading(true);

    const {
      lotNumber,
      nextLotNumber,
      lotSize,
      nextLotSize,
      lotStartDateMs,
      currentLotRowID,
    } = lotInfoForProcess;

    // add three new rows
    const currentLotFormDataEdited = {
      machemetryId: processId ?? "",
      lotSize: lotSize ? String(lotSize) : "",
      lotNumber: lotNumber ? String(lotNumber) : "",
      lotStartDateMs: lotStartDateMs ? String(lotStartDateMs) : "",
      isCompleted: true, // has finished
    };

    const nextLotBecomesCurrentLotFormDataEdited = {
      machemetryId: processId ?? "",
      lotSize: nextLotSize ? String(nextLotSize) : "",
      lotNumber: nextLotNumber ? String(nextLotNumber) : "",
      lotStartDateMs: now ? String(now) : "", // has started
      isCompleted: false,
    };

    /** if the last character is a number, we can increment it, otherwise return "" */
    const guessNewLotNumber = (lotNum: string) => {
      const lastChar = lotNum.slice(-1);
      const isLastCharNum = !isNaN(Number(lastChar));
      return isLastCharNum
        ? `${lotNum.slice(0, -1)}${
            // increment the last character if it's a number
            Number(lastChar) + 1
          }`
        : "";
    };

    const newNextLotFormData = {
      machemetryId: processId ?? "",
      lotSize: "", // ?: should we guess the next lot size somehow so they don't have to type it in every time?
      lotNumber: nextLotNumber ? guessNewLotNumber(nextLotNumber) : "", // ?: should we guess the next lot number like this?
      lotStartDateMs: "", // has not yet started
      isCompleted: false,
    };

    await handleFormSubmit(currentLotFormDataEdited);
    await handleFormSubmit(nextLotBecomesCurrentLotFormDataEdited);
    await handleFormSubmit(newNextLotFormData);

    // update the lot info on completion
    const newCurrentId = getNextId(currentLotRowID);
    const lotInfoAfterCompletion = {
      currentLotRowID: newCurrentId,
      lotNumber: nextLotNumber || "", // prevent submitting if next info is empty
      lotSize: nextLotSize as number, // prevent submitting if next info is empty
      nextLotNumber: null,
      nextLotSize: null, // ? do we want to autofill with the current lot size?
      lotStartDateMs: now,
    };

    // save to store
    dispatch(
      updateLotInfo({
        processId: processId ?? "",
        lotInfo: lotInfoAfterCompletion,
      })
    );

    return true; // ? return anything?
  };

  return {
    lotInfoForProcess,
    submitEditedLotInfo,
    completeTheLot,
    loading,
    setLoading,
  };
};

// - on first load -> fetch data from last two items
// - on edit -> submit data to replace last two items
// - on completeTheLot -> submit data to replace one item and start another fresh

/** data that can be submitted to the lot info back-end */
type LotFormDataType = {
  machemetryId: string;
  lotSize: string;
  lotNumber: string;
  lotStartDateMs: string;
  isCompleted: boolean;
};

function getNextId(currentLotRowID: string) {
  return currentLotRowID ? `${Number(currentLotRowID) + 1}` : "";
}

/** submit lotInfo data to the database for one process */
async function handleFormSubmit({
  machemetryId,
  lotSize,
  lotNumber,
  lotStartDateMs,
  isCompleted,
}: LotFormDataType) {
  const formData = getLotInfoFormDataToSubmit({
    machemetryId,
    lotSize,
    lotNumber,
    lotStartDateMs,
    isCompleted,
  });
  const url = LOT_SCRIPT_URLS_BY_PROCESS[machemetryId];
  return fetch(url, {
    method: "POST",
    mode: "no-cors",
    body: formData,
  }).catch(err => {
    console.error("Error:", err);
  });
}

const getLotInfoFormDataToSubmit = ({
  machemetryId,
  lotSize,
  lotNumber,
  lotStartDateMs,
  isCompleted,
}: LotFormDataType): FormData => {
  const formData = new FormData();
  // fix: always submit a new row by sending ID = ""
  formData.append("ID", "");

  formData.append("machemetryId", machemetryId.toString());
  formData.append("lotSize", lotSize?.toString() || "");
  formData.append("lotNumber", lotNumber?.toString() || "");
  formData.append("lotStartDateMs", lotStartDateMs?.toString() || "");
  formData.append("isCompleted", isCompleted.toString());

  return formData;
};

export function useLotInfoForProcessId(processId: string | null): LotInfoType {
  return (
    useSelector(state => {
      return processId && state.legacy.allLotInfo[processId];
    }, isEqual) || EMPTY_INITIAL_LOT_INFO
  );
}

export const useProcessIdOnSingleProcessPage = (): string | null => {
  const [singleProcess] = useVisibleProcesses({ getSingleProcessOnly: true });
  const isSingleProcessPage = useIsSingleProcessPage();
  return useMemo(
    () => (isSingleProcessPage ? singleProcess?.id : null),
    [isSingleProcessPage, singleProcess?.id]
  );
};

export const useLotInfoForSingleProcessId = (): LotInfoType => {
  const processId = useProcessIdOnSingleProcessPage();
  return useLotInfoForProcessId(processId);
};

/** text to show instead of "lot" for some customers */
export function useLotText() {
  const ckey = useCustomerKey();
  return useMemo(() => (ckey === "pciuk" ? "work order" : "lot"), [ckey]);
}
